import Quill from "quill";
import { SegmentoTela } from "../model/segmento.model";
import { Lud, TipoLud } from "../model/lud.model";

/**
 * Classe utilitária para manipulação de conteúdo HTML usando o componente Quill.
 */
export class QuillHtmlUtil {

    /**
     * O componente Quill coordena a edição de um conteúdo HTML.
     * 
     * O componente possui um atributo root.innerHTML que guarda o texto com formatações.
     * Para implementar novas funcionalidades, algumas vezes precisamos acessar este texto interno.
     * As principais necessidades são juntar segmentos, separar segmento e incluir quebra de linha dentro do segmento.
     * 
     * Porém, o componente usa a tag <P> tanto para o texto completo quanto para porções do texto (parágrafos dentro do segmento).
     * E esta implementação não é compatível com a implementação do SIJUT antigo.
     * 
     * É necessário que as tags <P> sejam removidas quando há mais que um parágrafo dentro do texto.
     * 
     * Adicionalmente, o uso de classes nos parágrafos também deve ser ignorada neste momento pelo novo SIJUT.
     * As classes, neste momento, devem estar atreladas ao SEGMENTO, e não ao parágrafo.
     */

    /**
     * remove as tags <P>. Caso haja mais que um <P>, inclui quebras de linha <BR/>
     * @param html string
     * @returns html ajustado
     */
    static ajustarHTML(html: string): string {
        let limpo = '';
        let pos = 0;
        while (pos < html.length) {
            const i = pos + html.substring(pos).indexOf('</p>');
            if (i >= 0) {
                const paragrafo = this.ajustarParagrafoHTML(html.substring(pos, i + 4));
                limpo += (limpo ? '<br />' + String.fromCharCode(10) : '') + paragrafo;
                pos = i + 4;
            } else {
                limpo += (limpo ? '<br />' + String.fromCharCode(10) : '') + html.substring(pos);
                pos = html.length;
            }
        }

        while (limpo.indexOf(' target="_blank"') >= 0) {
            limpo = limpo.replace(' target="_blank"', '');
        }

        // para o caso de uso dos atributos font-family, font-size e font-color dentro do segmento.
        limpo = this.removerEstilosMapeados(limpo);
        limpo = this.substituirClassePorStyle(limpo);

        return limpo;
    }

    /**
     * remove a tag <P> de um único parágrafo.
     * @param html 
     * @returns 
     */
    static ajustarParagrafoHTML(html: string): string {
        let i = html.indexOf('<p');
        if (i != 0) {
            return html;
        }
        i = html.indexOf('>');
        const f = html.length - 4;
        if (html.substring(f) != '</p>') {
            return html;
        }
        return (html.substring(i + 1, f));
    }

    static obterSelecaoTexto(idx: number, segmento: SegmentoTela): Lud | null {
        const quill = segmento.editor;
        if (quill == null) {
            return null;
        }
        const range = quill.getSelection(false);
        if (range) {
            const l = new Lud();
            l.idx = idx;
            l.quill = quill;
            l.inicio = range.index;
            l.fim = range.index + range.length;
            return l;
        }
        return null;
    }

    /**
     * Pinta de azul o fundo do texto selecionado.
     * @param selecao 
     */
    static destacarSelecao(selecao: Lud) {
        selecao.quill.formatText(selecao.inicio, selecao.fim - selecao.inicio, {color: 'white', 'background-color': 'royalblue'});
    }

    /**
     * Retira a cor azul do fundo do texto selecionado. Remove ainda as outras cores de fundo dentro da seleção.
     * @param selecao 
     */
    static tirarRealceSelecao(selecao: Lud) {
        selecao.quill.formatText(selecao.inicio, selecao.fim - selecao.inicio, {color: false, 'background-color': false});
    }

    /**
     * Aplica formatação de cor e plano de fundo a um objeto Lud de acordo com o tipo.
     * @param lud O objeto Lud a ser colorido.
     */
    static colorirLud(lud: Lud) {
        let color: string | false = false;
        let bgColor: string | false = false;
    
        switch (lud.tipo) {
            case TipoLud.LUD_NORMAS:
                [color, bgColor] = ['black', '#B0FF61'];
                break;
            case TipoLud.LUD_INTERNET:
                [color, bgColor] = ['black', 'lightpink'];
                break;
            case TipoLud.LUD_URL:
                [color, bgColor] = ['black', '#AFEAFF'];
                break;
            case TipoLud.LUD_PALAVRA_CHAVE:
                [color, bgColor] = ['black', 'yellow'];
                break;
        }
        
        lud.quill.formatText(lud.inicio, lud.fim - lud.inicio, {'background-color': bgColor, 'color': color});
    }

    /**
     * Localiza os LUDs originais confeccionados dentro de um segmento.
     * Procura somente tags <a>.
     * Na tela de confecção de LUDs só funciona ao abrir a tela,
     * pois esses LUDs são alterados para a tag <code> em seguida.
     * @param idx 
     * @param quill 
     * @returns 
     */
    static localizarLinksOriginais(idx: number, quill: Quill): Lud[] {
        const luds: Lud[] = [];
        const delta = quill.getContents();
        let ultimoLud: Lud | null = null;
        let index = 0; // Para rastrear a posição inicial de cada operação

        if (delta.ops) {
            // Percorre cada operação do delta
            delta.ops.forEach(op => {
                const length = op.insert.length || 1; // Calcula o comprimento do texto atual
                if (op.attributes && op.attributes['link']) {
                    const url = op.attributes['link'];
                    if (ultimoLud && ultimoLud.idx == idx && ultimoLud.fim == index && ultimoLud.url == url) {
                        ultimoLud.fim = index + length;
                    } else {
                        const l = new Lud();
                        l.quill = quill;
                        l.idx = idx;
                        l.inicio = index;
                        l.fim = index + length;
                        l.tipo = Lud.obterTipoLud(url); 
                        l.url = url;
                        luds.push(l);
                        ultimoLud = l;
                    }
                }
                index += length; // Atualiza o índice para a próxima operação
            });
        }
        return luds;
    }

    /**
     * Localiza os LUDs originais confeccionados dentro de um segmento.
     * Procura somente tags <a>.
     * Na tela de confecção de LUDs só funciona ao abrir a tela,
     * pois esses LUDs são alterados para a tag <code> em seguida.
     * @param idx 
     * @param quill 
     * @returns 
     */
    static ajustarLinksParaSalvar(luds: Lud[]) {
        for (const lud of luds) {
            if (lud.tipo != TipoLud.LUD_PALAVRA_CHAVE) {
                this.transformarDeCodeParaAncora(lud);
            }
            this.tirarRealceSelecao(lud);
        }
    }

    private static estiloClasses: { [key: string]: string } = {
        'ql-font-monospace': 'font-family: Monaco, Courier New, monospace;',
        'ql-font-serif': 'font-family: Georgia, Times New Roman, serif;',
        'ql-size-small': 'font-size: 0.75em;',
        'ql-size-large': 'font-size: 1.5em;',
        'ql-size-huge': 'font-size: 2.5em;'
    };

    static substituirClassePorStyle(html: string): string {
            
        const pattern = /<\s*\w+[^>]*(?:\s+class=(['"])[^'"]*\1)[^>]*>/g;
    
        return html.replace(pattern, (tag) => {
            // Captura o valor do atributo 'class' da tag
            const classMatch = tag.match(/class=['"]([^'"]+)['"]/);
            if (!classMatch) return tag;
    
            const classes = classMatch[1].split(/\s+/); // Divide as classes da tag
            let stylesToAdd = '';
    
            // Verifica se cada classe tem um estilo correspondente
            classes.forEach(className => {
                if (this.estiloClasses[className]) {
                    stylesToAdd += this.estiloClasses[className]; // Adiciona o estilo ao atributo style
                }
            });
    
            // Verifica se já existe o atributo 'style' na tag
            if (tag.includes('style="')) {
                // Se já existe o atributo 'style', adiciona os novos estilos
                return tag.replace(/style="([^"]*)"/, (match, existingStyles) => {
                    return `style="${existingStyles} ${stylesToAdd}"`;
                });
            } else {
                // Se não existe o atributo 'style', cria um novo atributo 'style'
                return tag.replace(/<\s*\w+/, (match) => {
                    return match + ` style="${stylesToAdd}"`; // Adiciona o atributo style com os estilos
                });
            }
        });
    }

    static removerEstilosMapeados(html: string): string {
        const pattern = /<\s*\w+[^>]*\sstyle=['"][^'"]*['"][^>]*>/g;
    
        return html.replace(pattern, (tag) => {
            // Captura o conteúdo do atributo 'style' da tag
            const styleMatch = tag.match(/style=['"]([^'"]+)['"]/);
            if (!styleMatch) return tag; // Retorna a tag original se não houver 'style'
    
            const estilos = styleMatch[1].split(";").map(style => style.trim()).filter(Boolean); // Divide os estilos
            const estilosRestantes = estilos.filter(estilo => !Object.values(this.estiloClasses).includes(estilo + ';')); // Remove estilos mapeados
    
            // Atualiza o atributo 'style'
            if (estilosRestantes.length > 0) {
                const novoStyle = estilosRestantes.join("; ") + ";";
                return tag.replace(/style=['"][^'"]*['"]/, `style="${novoStyle}"`);
            } else {
                // Remove o atributo 'style' completamente se não houver estilos restantes
                return tag.replace(/\sstyle=['"][^'"]*['"]/, '');
            }
        });
    }

    static transformarDeAncoraParaCode(l: Lud) {
        const quill = l.quill;
        quill.formatText(l.inicio, l.fim - l.inicio, { link: false, code: true });
    }

    static transformarDeCodeParaAncora(l: Lud) {
        const quill = l.quill;
        quill.formatText(l.inicio, l.fim - l.inicio, { code: false, link: l.url });
    }
}
